import { Store, select } from '@ngrx/store';
import { Observable, combineLatest, map, of } from 'rxjs';
import { Permission } from '../enums/permissions.enum';
import { AuthState } from '../state/auth/auth.state';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class UserPermissionService {
  // Temporary solution until backend roles are implemented
  private tempInventoryRoles: Permission[] = [
    Permission.TICKET_CREATE,
    Permission.TICKET_VIEW,
    Permission.TICKET_UPDATE,
    Permission.TICKET_DELETE,
    Permission.TICKET_TRANSACTION_CREATE,
    Permission.TICKET_TRANSACTION_VIEW,
    Permission.TICKET_TRANSACTION_UPDATE,
    Permission.TICKET_TRANSACTION_DELETE,
    Permission.TICKET_CATEGORY_VIEW,
    Permission.TICKET_CATEGORY_CREATE,
    Permission.TICKET_CATEGORY_EDIT,
  ];

  private tempTransactionsRoles: Permission[] = [
    Permission.TRANSACTION_CREATE,
    Permission.TRANSACTION_VIEW,
    Permission.TRANSACTION_UPDATE,
    Permission.TRANSACTION_DELETE,
    Permission.CASH_REGISTER_CREATE,
    Permission.CASH_REGISTER_VIEW,
    Permission.CASH_REGISTER_UPDATE,
    Permission.CASH_REGISTER_DELETE,
  ];

  constructor(private store: Store<{ auth: AuthState }>) {}

  /* Get user permissions as an observable (plus the temp roles currently) */
  private getPermissions$(): Observable<Permission[]> {
    return this.store.pipe(
      select((state) => state.auth.roles || []),
      map((roles) => {
        const mergedRoles = [...roles];
        for (const permission of this.tempInventoryRoles) {
          if (!mergedRoles.includes(permission)) {
            mergedRoles.push(permission);
          }
        }
        for (const permission of this.tempTransactionsRoles) {
          if (!mergedRoles.includes(permission)) {
            mergedRoles.push(permission);
          }
        }
        return mergedRoles;
      })
    );
  }

  /** Returns true if the user has a specific permission
   * Avoid using externally. Use the specific permission methods instead.
   * We shall create here an abstraction. For example isUserManager may check if 5 permissions are present.
   */
  hasPermission(permission: Permission): Observable<boolean> {
    return this.getPermissions$().pipe(map((permissions) => permissions.includes(permission)));
  }

  // Example usage
  employeeEdit(): Observable<boolean> {
    return this.hasPermission(Permission.EMPLOYEE_UPDATE);
  }

  companyAdmininstrator(): Observable<boolean> {
    return combineLatest([
      this.hasPermission(Permission.COMPANY_VIEW),
      this.hasPermission(Permission.STORE_VIEW),
      this.hasPermission(Permission.STORE_UPDATE),
      this.hasPermission(Permission.STORE_DELETE),
      this.hasPermission(Permission.STORE_CREATE),
      this.hasPermission(Permission.EMPLOYEE_VIEW),
    ]).pipe(map((results) => results.every((value) => value === true)));
  }

  employeeShiftManagement(): Observable<boolean> {
    return combineLatest([
      this.hasPermission(Permission.SHIFT_CREATE),
      this.hasPermission(Permission.SHIFT_UPDATE),
      this.hasPermission(Permission.SHIFT_DELETE),
      this.hasPermission(Permission.SHIFT_VIEW),
    ]).pipe(map((results) => results.every((value) => value === true)));
  }

  shiftViewOnly(): Observable<boolean> {
    return this.hasPermission(Permission.SHIFT_VIEW);
  }

  employeeManagement(): Observable<boolean> {
    return combineLatest([
      this.hasPermission(Permission.EMPLOYEE_CREATE),
      this.hasPermission(Permission.EMPLOYEE_DELETE),
      this.hasPermission(Permission.EMPLOYEE_UPDATE),
      this.hasPermission(Permission.EMPLOYEE_VIEW),
    ]).pipe(map((results) => results.every((value) => value === true)));
  }

  inventoryManagement(): Observable<boolean> {
    return combineLatest([
      this.hasPermission(Permission.TICKET_CREATE),
      this.hasPermission(Permission.TICKET_DELETE),
      this.hasPermission(Permission.TICKET_UPDATE),
      this.hasPermission(Permission.TICKET_VIEW),
      this.hasPermission(Permission.TICKET_TRANSACTION_CREATE),
      this.hasPermission(Permission.TICKET_TRANSACTION_VIEW),
      this.hasPermission(Permission.TICKET_TRANSACTION_UPDATE),
      this.hasPermission(Permission.TICKET_TRANSACTION_DELETE),
      this.hasPermission(Permission.TICKET_CATEGORY_VIEW),
      this.hasPermission(Permission.TICKET_CATEGORY_CREATE),
      this.hasPermission(Permission.TICKET_CATEGORY_EDIT),
    ]).pipe(map((results) => results.every((value) => value === true)));
  }

  transactionsManagement(): Observable<boolean> {
    return combineLatest([
      this.hasPermission(Permission.TRANSACTION_CREATE),
      this.hasPermission(Permission.TRANSACTION_VIEW),
      this.hasPermission(Permission.TRANSACTION_UPDATE),
      this.hasPermission(Permission.TRANSACTION_DELETE),
      this.hasPermission(Permission.CASH_REGISTER_CREATE),
      this.hasPermission(Permission.CASH_REGISTER_VIEW),
      this.hasPermission(Permission.CASH_REGISTER_UPDATE),
      this.hasPermission(Permission.CASH_REGISTER_DELETE),
    ]).pipe(map((results) => results.every((value) => value === true)));
  }

  notificationView(): Observable<boolean> {
    return this.hasPermission(Permission.NOTIFICATION_VIEW);
  }
}
